/**
 * BuildingDataPanel
 * Formular für Gebäudedaten (Kontextdaten) — editierbar und lesbar im ContentPanel.
 * Stil angelehnt an DIN-Spec-Berechnung: Label über Input, abgerundete Felder
 * mit hellem Grau-Hintergrund, gruppierte Felder nebeneinander.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react'
import { useAnnotationStore } from '../../store/annotations'
import { BUILDING_DATA_SCHEMA, BuildingDataField } from '../../types/annotations'

/** Fields to display in a single row (side-by-side) */
const ROW_GROUPS: string[][] = [
  ['bri', 'bgf'],
]

/** Build a lookup: fieldKey → group index (or -1 if standalone) */
function getGroupIndex(key: string): number {
  return ROW_GROUPS.findIndex(g => g.includes(key))
}

export function BuildingDataPanel() {
  const buildingData = useAnnotationStore(s => s.buildingData)
  const updateBuildingField = useAnnotationStore(s => s.updateBuildingField)

  const [isEditing, setIsEditing] = useState(false)

  // updateBuildingField macht bereits debounced PUT /building-data
  const handleChange = useCallback((key: string, value: string) => {
    updateBuildingField(key, value)
  }, [updateBuildingField])

  const filledCount = BUILDING_DATA_SCHEMA.filter(f => (buildingData.fields[f.key] || '').trim()).length
  const totalCount = BUILDING_DATA_SCHEMA.length

  // Build render list: group some fields into rows
  const rendered = new Set<string>()
  const rows: (BuildingDataField | BuildingDataField[])[] = []

  for (const field of BUILDING_DATA_SCHEMA) {
    if (rendered.has(field.key)) continue
    const gi = getGroupIndex(field.key)
    if (gi >= 0) {
      const group = ROW_GROUPS[gi]
        .map(k => BUILDING_DATA_SCHEMA.find(f => f.key === k)!)
        .filter(Boolean)
      group.forEach(f => rendered.add(f.key))
      rows.push(group)
    } else {
      rendered.add(field.key)
      rows.push(field)
    }
  }

  return (
    <div data-component="BuildingDataPanel" className="ghost-panel">
      {/* Header */}
      <div className="ghost-form-header">
        <span className="ghost-form-header__info">
          {filledCount}/{totalCount} ausgefüllt
        </span>
        <button
          className="ghost-form-header__btn"
          onClick={() => setIsEditing(v => !v)}
          title={isEditing ? 'Lese-Modus' : 'Bearbeiten'}
        >
          {isEditing ? (
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
            </svg>
          ) : (
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
            </svg>
          )}
        </button>
      </div>

      {/* Fields — im Lese-Modus nur ausgefüllte anzeigen */}
      <div className="ghost-form-body">
        {rows.map((row, i) => {
          if (Array.isArray(row)) {
            const visibleFields = isEditing
              ? row
              : row.filter(f => (buildingData.fields[f.key] || '').trim())
            if (visibleFields.length === 0) return null
            return (
              <div className="ghost-form-row" key={row.map(f => f.key).join('-')}>
                {visibleFields.map(field => (
                  <FieldCell
                    key={field.key}
                    field={field}
                    value={buildingData.fields[field.key] || ''}
                    isEditing={isEditing}
                    onChange={handleChange}
                  />
                ))}
              </div>
            )
          }
          const val = (buildingData.fields[row.key] || '').trim()
          if (!isEditing && !val) return null
          return (
            <FieldCell
              key={row.key}
              field={row}
              value={buildingData.fields[row.key] || ''}
              isEditing={isEditing}
              onChange={handleChange}
            />
          )
        })}
      </div>
    </div>
  )
}

// ── AddressMap ────────────────────────────────────────────────────────────────

const TILE_SIZE = 256
const MAP_W = 400
const MAP_H = 200
const ZOOM = 16

function lng2tile(lon: number, z: number) { return ((lon + 180) / 360) * (1 << z) }
function lat2tile(lat: number, z: number) {
  const r = (lat * Math.PI) / 180
  return ((1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2) * (1 << z)
}

function AddressMap({ lat, lon }: { lat?: number | null; lon?: number | null }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    if (!lat || !lon || !canvasRef.current) return
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    canvas.width = MAP_W * 2
    canvas.height = MAP_H * 2
    ctx.scale(2, 2)

    const centerTileX = lng2tile(lon, ZOOM)
    const centerTileY = lat2tile(lat, ZOOM)
    const offsetX = MAP_W / 2 - (centerTileX % 1) * TILE_SIZE
    const offsetY = MAP_H / 2 - (centerTileY % 1) * TILE_SIZE
    const startTileX = Math.floor(centerTileX)
    const startTileY = Math.floor(centerTileY)

    const tilesX = Math.ceil(MAP_W / TILE_SIZE) + 1
    const tilesY = Math.ceil(MAP_H / TILE_SIZE) + 1

    // Zwei Layer: Basis (no labels) + Labels
    const layers = [
      `https://a.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}@2x.png`,
      `https://a.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}@2x.png`,
    ]

    let layersDone = 0
    for (const urlTpl of layers) {
      let tilesDone = 0
      const totalTiles = tilesX * tilesY
      for (let dx = -1; dx < tilesX - 1; dx++) {
        for (let dy = -1; dy < tilesY - 1; dy++) {
          const tx = startTileX + dx
          const ty = startTileY + dy
          const url = urlTpl.replace('{z}', String(ZOOM)).replace('{x}', String(tx)).replace('{y}', String(ty))
          const img = new Image()
          img.crossOrigin = 'anonymous'
          img.onload = () => {
            const px = offsetX + dx * TILE_SIZE
            const py = offsetY + dy * TILE_SIZE
            ctx.drawImage(img, px, py, TILE_SIZE, TILE_SIZE)
            tilesDone++
            if (tilesDone === totalTiles) {
              layersDone++
              if (layersDone === layers.length) {
                // Marker zeichnen
                ctx.beginPath()
                ctx.arc(MAP_W / 2, MAP_H / 2, 6, 0, Math.PI * 2)
                ctx.fillStyle = '#2563eb'
                ctx.fill()
                ctx.strokeStyle = '#fff'
                ctx.lineWidth = 2
                ctx.stroke()
                // Halo
                ctx.beginPath()
                ctx.arc(MAP_W / 2, MAP_H / 2, 12, 0, Math.PI * 2)
                ctx.fillStyle = 'rgba(37, 99, 235, 0.15)'
                ctx.fill()
              }
            }
          }
          img.onerror = () => { tilesDone++ }
          img.src = url
        }
      }
    }
  }, [lat, lon])

  if (!lat || !lon) {
    return (
      <div className="building-map">
        <div className="building-map__empty">Kein Standort hinterlegt</div>
      </div>
    )
  }

  return (
    <div className="building-map">
      <canvas
        ref={canvasRef}
        className="building-map__canvas"
        style={{ width: MAP_W, height: MAP_H }}
      />
    </div>
  )
}

// ── FieldCell ──────────────────────────────────────────────────────────────

function FieldCell({
  field,
  value,
  isEditing,
  onChange,
}: {
  field: BuildingDataField
  value: string
  isEditing: boolean
  onChange: (key: string, value: string) => void
}) {
  const hasValue = value.trim() !== ''

  return (
    <div className="ghost-field" data-field={field.key}>
      <label className="ghost-field__label">{field.label}</label>

      {isEditing ? (
        field.type === 'select' && field.options ? (
          <select
            className="ghost-field__input ghost-field__select"
            value={value}
            onChange={e => onChange(field.key, e.target.value)}
          >
            <option value="">— Auswählen —</option>
            {field.options.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        ) : (
          <div className={`ghost-field__input-wrap${field.unit ? ' ghost-field__input-wrap--unit' : ''}`}>
            <input
              className="ghost-field__input"
              value={value}
              onChange={e => onChange(field.key, e.target.value)}
            />
            {field.unit && <span className="ghost-field__unit">{field.unit}</span>}
          </div>
        )
      ) : (
        <div className={`ghost-field__value${hasValue ? '' : ' ghost-field__value--empty'}`}>
          {hasValue ? value : '–'}
          {hasValue && field.unit && <span className="ghost-field__unit">{field.unit}</span>}
        </div>
      )}

      {field.hint && (
        <span className="ghost-field__hint">{field.hint}</span>
      )}
    </div>
  )
}
